import React from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { ExpedienteDespacho } from '../PaginaDespachoPortuario'

interface Props {
  expediente: ExpedienteDespacho
}

export const HojaFacturaComercial: React.FC<Props> = ({ expediente }) => {
  const factura = expediente.documentos.factura
  const packing = expediente.documentos.packingList
  const valorUSD = factura.valorUSD || 68400
  const folio = factura.folio || 'FAC-2026-8812'
  const incoterm = factura.incoterm || 'CIF Rotterdam (Incoterms 2020)'

  return (
    <div className="relative w-full bg-[#fdfdfa] text-slate-900 shadow-2xl rounded-sm p-6 sm:p-10 font-sans border border-slate-300 print:border-none print:shadow-none print:p-4 text-[10px] sm:text-[11px] leading-tight select-none">
      {/* Marca de Agua Comercial */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03] overflow-hidden">
        <div className="text-[100px] font-black uppercase text-slate-900 -rotate-30 select-none tracking-widest whitespace-nowrap">
          ORIGINAL INVOICE
        </div>
      </div>

      {/* Membrete Corporativo */}
      <div className="border-b-2 border-slate-900 pb-3 mb-3 flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded bg-slate-900 text-white font-black text-sm flex items-center justify-center font-serif">
              ✦
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-black tracking-tight text-slate-950 uppercase">
                {expediente.actores.emisor.nombre}
              </h2>
              <p className="text-[8.5px] font-mono text-slate-600">
                RFC: <strong className="text-slate-950">{expediente.actores.emisor.rfc}</strong> · REGISTRO FEDERAL DE
                CONTRIBUYENTES
              </p>
            </div>
          </div>
          <p className="text-[8px] text-slate-500">
            PARQUE INDUSTRIAL LOGÍSTICO MANZANILLO, COLIMA, MÉXICO · TEL: +52 (314) 331-9000
          </p>
        </div>

        <div className="text-right">
          <span className="font-mono text-xs font-black text-purple-900 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded">
            COMMERCIAL INVOICE
          </span>
          <p className="font-mono text-xs sm:text-sm font-black text-slate-950 mt-1">
            FOLIO: <span className="text-purple-950">{folio}</span>
          </p>
          <p className="font-mono text-[8px] text-slate-500">FECHA: {factura.fecha || '02/10/2026'}</p>
        </div>
      </div>

      {/* Bloque Cuadrícula: Comprador / Receptor y Condiciones de Entrega */}
      <div className="border-2 border-slate-900 mb-3 divide-y-2 divide-slate-900">
        <div className="grid grid-cols-12 divide-x-2 divide-slate-900">
          {/* Bill To / Consignatario */}
          <div className="col-span-7 p-2 space-y-1">
            <span className="block text-[7.5px] font-bold uppercase text-slate-500">
              SOLD TO / CONSIGNEE (BUYER / IMPORTER):
            </span>
            <p className="font-bold text-xs text-slate-950 uppercase">{expediente.actores.receptor.nombre}</p>
            <p className="text-[8.5px] text-slate-700 font-mono">
              TAX ID / VAT: <strong className="text-slate-950">{expediente.actores.receptor.rfc}</strong>
            </p>
            <p className="text-[8px] text-slate-500">{expediente.actores.receptor.domicilio}</p>
            <p className="text-[8px] text-purple-700 font-medium">
              ALUMNO RECEPTOR: {expediente.actores.receptor.alumnoNombre || 'Alumno Asignado'}
            </p>
          </div>

          {/* Términos de Envío y Pago */}
          <div className="col-span-5 p-2 space-y-1 bg-slate-50/70 font-mono text-[8.5px]">
            <div>
              <span className="block text-[7px] font-sans font-bold uppercase text-slate-500">INCOTERMS 2020:</span>
              <span className="font-bold text-slate-950">{incoterm}</span>
            </div>
            <div>
              <span className="block text-[7px] font-sans font-bold uppercase text-slate-500">PAYMENT TERMS:</span>
              <span className="font-bold text-slate-800">IRREVOCABLE LETTER OF CREDIT AT SIGHT</span>
            </div>
            <div>
              <span className="block text-[7px] font-sans font-bold uppercase text-slate-500">PORT OF LOADING:</span>
              <span className="font-bold text-slate-800">MANZANILLO, MEXICO (MXZLO)</span>
            </div>
            <div>
              <span className="block text-[7px] font-sans font-bold uppercase text-slate-500">PORT OF DISCHARGE:</span>
              <span className="font-bold text-slate-800">{expediente.buque.puertoDestino}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla Desglose de Mercancías */}
      <div className="border-2 border-slate-900 mb-3">
        <div className="bg-slate-100 border-b border-slate-900 grid grid-cols-12 divide-x divide-slate-400 font-bold text-[8px] text-slate-700 uppercase p-1">
          <div className="col-span-1 text-center">ITEM</div>
          <div className="col-span-2">HS CODE</div>
          <div className="col-span-5">DESCRIPTION OF GOODS</div>
          <div className="col-span-2 text-right">QUANTITY</div>
          <div className="col-span-2 text-right">TOTAL USD</div>
        </div>

        <div className="grid grid-cols-12 divide-x divide-slate-300 font-mono text-[9px] p-2 min-h-[110px] bg-white">
          <div className="col-span-1 text-center font-bold">01</div>
          <div className="col-span-2 font-bold text-blue-900">
            {expediente.regimen === 'IR_RFE' ? '0804.40.01' : '2208.90.03'}
          </div>
          <div className="col-span-5 space-y-1">
            <p className="font-bold text-slate-950 uppercase">{expediente.titulo}</p>
            <p className="text-[8px] text-slate-500">
              CONTAINER: {expediente.contenedor.numero} · SEALS: {expediente.contenedor.sellos}
            </p>
            <p className="text-[7.5px] text-slate-500">
              PACKED IN {packing.bultos.toLocaleString()} EXPORT CARTONS ON 20 TREATED PALLETS.
            </p>
          </div>
          <div className="col-span-2 text-right font-bold">
            <p>{packing.pesoNetoKg.toLocaleString()} KG</p>
            <p className="text-[7.5px] text-slate-500 font-sans">{packing.bultos} BULTOS</p>
          </div>
          <div className="col-span-2 text-right font-bold text-xs text-slate-950">
            <p>${valorUSD.toLocaleString()}.00</p>
            <p className="text-[7.5px] text-slate-500 font-sans">USD NET</p>
          </div>
        </div>

        {/* Totales de Factura */}
        <div className="border-t-2 border-slate-900 bg-slate-900 text-white p-2 font-mono flex items-center justify-between text-xs font-bold">
          <div>
            <span className="font-sans text-[8px] uppercase tracking-wider block text-slate-400">
              TOTAL INVOICE AMOUNT (USD):
            </span>
            <span className="text-[9px] text-amber-300 uppercase">
              {valorUSD.toLocaleString()} US DOLLARS NET
            </span>
          </div>
          <span className="text-base font-black text-amber-400">${valorUSD.toLocaleString()}.00 USD</span>
        </div>
      </div>

      {/* Datos Bancarios y Certificación */}
      <div className="border border-slate-900 grid grid-cols-12 divide-x divide-slate-400 font-mono text-[8px] bg-slate-50 mb-3">
        <div className="col-span-6 p-2 space-y-0.5">
          <span className="font-sans font-bold uppercase text-[7px] text-slate-500 block">
            WIRE TRANSFER BANK DETAILS:
          </span>
          <p className="font-bold text-slate-900">BANK: BANCO NACIONAL DE COMERCIO EXTERIOR (BANCOMEXT)</p>
          <p className="text-slate-600">SWIFT CODE: BCMXMXMMXXX · ACCOUNT: 012-9840129-44</p>
          <p className="text-slate-600">BENEFICIARY: {expediente.actores.emisor.nombre}</p>
        </div>

        <div className="col-span-6 p-2 space-y-0.5">
          <span className="font-sans font-bold uppercase text-[7px] text-slate-500 block">
            PACKING LIST SUMMARY:
          </span>
          <p className="font-bold text-slate-900">
            TOTAL PACKAGES: {packing.bultos.toLocaleString()} CARTONS
          </p>
          <p className="text-slate-600">NET WEIGHT: {packing.pesoNetoKg.toLocaleString()} KGS</p>
          <p className="text-slate-600">GROSS WEIGHT: {expediente.contenedor.pesoBrutoKg.toLocaleString()} KGS</p>
        </div>
      </div>

      {/* Declaración Jurada y Sello */}
      <div className="border-2 border-slate-900 p-2 font-mono text-[7px] relative bg-white">
        <div className="grid grid-cols-12 gap-3 items-center">
          <div className="col-span-2 flex flex-col items-center justify-center p-1 bg-white border border-slate-300 rounded">
            <QRCodeSVG value={`https://cfdi.sat.gob.mx/consulta?uuid=8a1b2c3d-4e5f-6a7b-8c9d-0e1f2a3b4c5d&re=${expediente.actores.emisor.rfc}&rr=${expediente.actores.receptor.rfc}&tt=${valorUSD}`} size={60} />
            <span className="text-[6px] font-sans font-bold text-slate-500 mt-0.5">CFDI EXPORTACIÓN</span>
          </div>

          <div className="col-span-6 space-y-1 text-slate-600 font-sans">
            <p className="font-bold text-slate-900 text-[8px]">EXPORTER'S LEGAL DECLARATION:</p>
            <p className="leading-tight text-[7px]">
              "We hereby certify that this invoice is true and correct in all particulars and that the prices quoted represent the true and actual value of the merchandise specified above."
            </p>
            <p className="text-[6.5px] font-mono text-slate-500">
              ORIGIN OF GOODS: UNITED MEXICAN STATES (MÉXICO).
            </p>
          </div>

          <div className="col-span-4 border-l border-slate-300 pl-3 text-center">
            <div className="h-9 flex items-center justify-center">
              <span className="font-serif italic text-base text-purple-950 font-bold tracking-tight">
                Lic. Carlos Morales
              </span>
            </div>
            <div className="border-t border-slate-800 pt-0.5">
              <span className="font-sans font-bold text-[7.5px] uppercase text-slate-800 block">
                AUTHORIZED EXPORT SIGNATURE
              </span>
              <span className="text-[6.5px] text-slate-500 font-mono">
                {expediente.actores.emisor.alumnoNombre || 'Alumno Emisor'}
              </span>
            </div>
          </div>
        </div>

        {/* Sello Redondo Exporter */}
        <div className="absolute right-36 top-[-26px] pointer-events-none transform -rotate-6">
          <div className="h-20 w-20 rounded-full border-2 border-dashed border-purple-900 bg-purple-50/80 backdrop-blur-sm p-1 flex flex-col items-center justify-center text-center text-purple-950 shadow-md">
            <span className="text-[5.5px] font-black uppercase tracking-wider">OFFICIAL EXPORT</span>
            <span className="text-[7.5px] font-black uppercase text-purple-900">INVOICE ISSUED</span>
            <span className="text-[5.5px] font-mono">AUTHORIZED 2026</span>
          </div>
        </div>
      </div>

      <div className="mt-2 text-center text-[7px] font-mono text-slate-400">
        FACTURA COMERCIAL INTERNACIONAL CON REQUISITOS FISCALES PARA DESPACHO ADUANERO DE EXPORTACIÓN EN MANZANILLO.
      </div>
    </div>
  )
}
